/* eslint-disable indent */
import {
  Box,
  Divider,
  Grid,
  makeStyles,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import React, { useRef } from 'react';
import { connect } from 'react-redux';
import InputAdornmentChip from 'components/InputAdornmentChip';
import { Formik } from 'formik';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import i18n from 'i18n';
import Button from 'components/Button';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import {
  EnvironmentFields,
  FormSchedulePublishProps,
  ListPublicationScheduleProps,
  PublicationScheduleFields,
} from 'modules/environments/interfaces';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { useParams } from 'react-router';
import { ModalFields } from 'modules/modal/interfaces';
import Permission from 'components/ValidPermission';
import ApplicationState from 'store/interfaces';
import DatePicker from 'components/DatePicker';
import { dateFormatWithAt } from 'utils/helpers';
import moment from 'moment';
import {
  cancelPublicationScheduleRequest,
  getPublicationScheduleRequest,
  setPublicationScheduleRequest,
} from 'modules/environments/actions';
import { useTableFilters } from 'hooks/useTableFilters';
import clsx from 'clsx';
import PageTitle from 'components/PageTitle';
import { TableCellHeaderProps } from 'components/TableItems/interfaces';
import { PermissionsData } from 'modules/authenticate/interfaces';
import GenericForm from 'skyone-lib/lib/GenericForm';
import styles from './styles';
import schema from './schema';
import StatusSchedule from './status';

const useStyles = makeStyles(styles);

const optionsBlockTtlMinutes = [5, 15, 30, 60];

const initialQuery = {
  offset: 0,
  limit: 10,
  search: '',
};

interface Props {
  environmentSelected: EnvironmentFields | null;
  permissions?: PermissionsData;
  publicationSchedule: ListPublicationScheduleProps;
  isLoadingPublicationSchedule: boolean;
  highlightSection?: string;
  setPublicationScheduleRequest: (props) => void;
  cancelPublicationScheduleRequest: (props) => void;
  getPublicationScheduleRequest: (props) => void;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
  setCurrentTab: React.Dispatch<React.SetStateAction<number>>;
}

const SchedulePublication = ({
  environmentSelected,
  permissions,
  publicationSchedule,
  isLoadingPublicationSchedule,
  highlightSection,
  setPublicationScheduleRequest,
  cancelPublicationScheduleRequest,
  getPublicationScheduleRequest,
  modalOpen,
  modalClose,
  setCurrentTab,
}: Props) => {
  const classes = useStyles();
  const { id } = useParams<{ id: string }>();
  const { query, handleLimitChange, handlePageChange } = useTableFilters(
    initialQuery
  );

  const sectionRef = useRef<HTMLDivElement>(null);

  const scrollToSection = () => {
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };
  if (highlightSection === 'scheduleListing') {
    scrollToSection();
  }

  const initialValues: FormSchedulePublishProps = {
    notification_emails: [],
    schedule_datetime: null,
    replace_instances: {
      block_instances: false,
      blocked_ttl_minutes: 30,
      notify_users: false,
      instance_block_user_message: '',
    },
  };

  const headers: Array<TableCellHeaderProps & { hidden?: boolean }> = [
    {
      key: 'request_uuid',
      i18n: 'IDENTIFIER_KEY',
      centerTitle: false,
    },
    {
      key: 'status',
      i18n: 'STATUS_KEY',
      centerTitle: true,
    },
    {
      key: 'operator_email',
      i18n: 'REQUESTER_KEY',
      centerTitle: false,
    },
    {
      key: 'schedule_datetime',
      i18n: 'SCHEDULE_DATETIME_KEY',
      centerTitle: true,
    },
    {
      key: 'timezone',
      i18n: 'TIMEZONE_KEY',
      centerTitle: true,
    },
    {
      key: 'action',
      i18n: ' ',
      centerTitle: true,
      hidden: !!permissions && !permissions.cancel_image_publication_schedule,
    },
  ].filter(header => !header.hidden);

  const getPublicationSchedule = () => {
    getPublicationScheduleRequest({ id, query });
  };

  React.useEffect(() => {
    getPublicationSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getPublicationScheduleRequest, query]);

  return (
    <>
      <Permission value="create_image_publication_schedule">
        <Grid container className={classes.formGrid}>
          <Grid container>
            <Formik
              initialValues={initialValues}
              enableReinitialize
              validationSchema={schema}
              validateOnChange={false}
              onSubmit={(values, { setFieldValue }) => {
                const scheduleDatetimeWithoutTimezone = moment(
                  values?.schedule_datetime
                ).format('YYYY-MM-DDTHH:mm:ss');

                modalOpen({
                  title: i18n.t('CONFIRM_PUBLICATION_SCHEDULE_TITLE_KEY'),
                  maxWidth: 'sm',
                  content: (
                    <Typography className={classes.headerText}>
                      {i18n.t('CONFIRM_PUBLICATION_SCHEDULE_CONTENT_KEY', {
                        datetime: dateFormatWithAt(values.schedule_datetime),
                      })}
                    </Typography>
                  ),
                  footer: (
                    <Grid>
                      <Button
                        id="submit-button"
                        submitButton
                        onClick={() => {
                          setPublicationScheduleRequest({
                            id,
                            query,
                            values: {
                              ...values,
                              schedule_datetime: scheduleDatetimeWithoutTimezone,
                            },
                          });
                          setFieldValue('schedule_datetime', null);
                          modalClose();
                        }}
                      >
                        {i18n.t('CONFIRM_KEY')}
                      </Button>
                      <Button
                        id="cancel-button"
                        variant="outlined"
                        onClick={modalClose}
                      >
                        {i18n.t('CANCEL_KEY')}
                      </Button>
                    </Grid>
                  ),
                });
              }}
            >
              {({ setFieldValue, values, submitForm, errors }) => (
                <>
                  <Grid container spacing={2} className={classes.emailField}>
                    <InputAdornmentChip
                      gridXs={12}
                      label={i18n.t('EMAILS_TO_NOTIFY_CONCLUSION_KEY')}
                      stateKey="notification_emails"
                      type="email"
                      values={
                        environmentSelected?.trusted_users?.split(',') || []
                      }
                      setFieldValue={setFieldValue}
                    />
                  </Grid>
                  <Grid
                    container
                    spacing={2}
                    className={clsx(classes.date, {
                      [classes.dateError]: !!errors.schedule_datetime,
                    })}
                  >
                    <Grid item xs={6}>
                      <DatePicker
                        id="schedule_datetime"
                        type="datetimeFilter"
                        label={i18n.t('DATETIME_TO_SCHEDULE_KEY')}
                        keyValue="schedule_datetime"
                        value={values.schedule_datetime}
                        disablePast
                        shrink={
                          values?.schedule_datetime !== '' &&
                          values?.schedule_datetime !== null &&
                          values?.schedule_datetime !== undefined
                        }
                        error={!!errors.schedule_datetime}
                        helperText={errors.schedule_datetime}
                        setFieldsValue={(field, value) => {
                          setFieldValue(field, value);
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        id="timezone"
                        label={i18n.t('TIMEZONE_KEY')}
                        variant="outlined"
                        disabled={true}
                        value={environmentSelected?.behavior?.timezone}
                        className={classes.statusTextField}
                        fullWidth
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={3} className={classes.optimizeChecks}>
                    <GenericForm
                      fields={values}
                      screenFields={[
                        {
                          gridXs: 9,
                          label: i18n.t('BLOCK_INSTANCES_KEY'),
                          stateKey: 'replace_instances.block_instances',
                          isCheckbox: true,
                        },
                      ]}
                      setFieldValue={setFieldValue}
                      errors={errors}
                    />
                    <Tooltip
                      arrow
                      title={i18n.t('BLOCK_INSTANCES_TOOLTIP_KEY')}
                    >
                      <HelpOutlineIcon className={classes.helpIconForm} />
                    </Tooltip>
                  </Grid>
                  {values?.replace_instances?.block_instances && (
                    <Grid
                      container
                      justify="flex-start"
                      spacing={2}
                      className={classes.executedPublicationsModal}
                    >
                      <Grid
                        item
                        xs={6}
                        className={classes.executedPublications}
                      >
                        <TextField
                          id="executed-instance-blocking-publications"
                          label={i18n.t(
                            'ENVIRONMENTS_PAGE.EXECUTED_MONTHLY_PUBLICATION_KEY'
                          )}
                          value={
                            environmentSelected &&
                            environmentSelected?.publication
                              ?.limit_instance_blocking_publications -
                              environmentSelected?.publication
                                ?.executed_instance_blocking_publications
                          }
                          variant="outlined"
                          type="number"
                          InputProps={{ readOnly: true }}
                          disabled={true}
                          helperText={
                            <Typography
                              component="div"
                              className={classes.limitText}
                            >
                              {`${i18n.t('MAX_PUBLICATIONS_KEY')}: `}
                              <Box
                                fontWeight="bold"
                                display="inline"
                                className={classes.limitTextBold}
                              >
                                {
                                  environmentSelected?.publication
                                    ?.limit_instance_blocking_publications
                                }
                              </Box>
                            </Typography>
                          }
                        />
                      </Grid>
                      {environmentSelected?.publication
                        ?.limit_instance_blocking_publications_daily !==
                        null && (
                        <Grid
                          item
                          xs={6}
                          className={classes.executedPublications}
                        >
                          <TextField
                            id="executed-instance-blocking-publications-daily"
                            label={i18n.t(
                              'ENVIRONMENTS_PAGE.EXECUTED_DAILY_PUBLICATION_KEY'
                            )}
                            value={
                              environmentSelected &&
                              Number(
                                environmentSelected?.publication
                                  ?.limit_instance_blocking_publications_daily
                              ) -
                                environmentSelected?.publication
                                  ?.executed_instance_blocking_publications_daily
                            }
                            variant="outlined"
                            type="number"
                            InputProps={{ readOnly: true }}
                            disabled={true}
                            helperText={
                              <Typography
                                component="div"
                                className={classes.limitText}
                              >
                                {`${i18n.t('MAX_DAILY_PUBLICATIONS_KEY')}: `}
                                <Box
                                  fontWeight="bold"
                                  display="inline"
                                  className={classes.limitTextBold}
                                >
                                  {
                                    environmentSelected?.publication
                                      ?.limit_instance_blocking_publications_daily
                                  }
                                </Box>
                              </Typography>
                            }
                          />
                        </Grid>
                      )}
                      <Grid item xs={12} direction="column" spacing={2}>
                        <Grid container direction="row" alignItems="center">
                          <Grid item>
                            <Typography className={classes.blockDuration}>
                              {i18n.t('BLOCK_DURATION_KEY')}
                            </Typography>
                          </Grid>
                          <Grid item>
                            <GenericForm
                              fields={values}
                              screenFields={[
                                {
                                  gridXs: 'auto',
                                  label: '',
                                  stateKey:
                                    'replace_instances.blocked_ttl_minutes',
                                  isSelect: true,
                                  multipleSelect: false,
                                  options: optionsBlockTtlMinutes.map(
                                    option => ({
                                      label: option.toString(),
                                      value: option,
                                    })
                                  ),
                                },
                              ]}
                              setFieldValue={setFieldValue}
                              errors={errors}
                            />
                          </Grid>
                        </Grid>
                        <Grid item>
                          <GenericForm
                            fields={values}
                            screenFields={[
                              {
                                gridXs: 9,
                                label: i18n.t('NOTIFY_USERS_KEY'),
                                stateKey: 'replace_instances.notify_users',
                                isCheckbox: true,
                              },
                            ]}
                            setFieldValue={(key, value) => {
                              if (
                                key === 'replace_instances.notify_users' &&
                                !value
                              ) {
                                setFieldValue(
                                  'replace_instances.instance_block_user_message',
                                  ''
                                );
                              }
                              setFieldValue(key, value);
                            }}
                            errors={errors}
                          />
                        </Grid>
                        {values?.replace_instances?.notify_users && (
                          <Grid item xs={12}>
                            <GenericForm
                              fields={values}
                              screenFields={[
                                {
                                  gridXs: 12,
                                  label: i18n.t(
                                    'ENVIRONMENTS_PAGE.USER_MSG_IMAGE_KEY'
                                  ),
                                  stateKey:
                                    'replace_instances.instance_block_user_message',
                                },
                              ]}
                              setFieldValue={setFieldValue}
                              errors={errors}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  )}
                  <Grid
                    container
                    justify="flex-end"
                    className={classes.buttons}
                  >
                    <Button
                      id="submit-button"
                      onClick={submitForm}
                      submitButton
                    >
                      {i18n.t('SCHEDULE_KEY')}
                    </Button>
                  </Grid>
                </>
              )}
            </Formik>
          </Grid>
        </Grid>
      </Permission>
      <Grid container>
        <Divider className={classes.divider} />
      </Grid>
      <Grid container>
        <Grid container className={classes.actionsGrid}>
          <Grid item>
            <PageTitle
              title={i18n.t('SCHEDULES_KEY')}
              subtitle={i18n.t('SCHEDULES_LISTING_KEY')}
            />
          </Grid>
          <Grid item>
            <Permission value="list_models">
              <Button
                className={classes.seeModelsButtons}
                variant="outlined"
                onClick={() => setCurrentTab(2)}
              >
                {i18n.t('SEE_GENERATED_MODELS_KEY')}
              </Button>
            </Permission>
          </Grid>
        </Grid>
        <Grid container>
          <Permission value="list_image_publication_schedule">
            <Grid container innerRef={sectionRef}>
              <TableItems
                header={headers.map(header => (
                  <TableCell
                    centerTitle={header?.centerTitle}
                    key={header?.key}
                  >
                    {i18n.t(header?.i18n)}
                  </TableCell>
                ))}
                renderItems={(item: PublicationScheduleFields) => (
                  <TableRow key={item?.schedule_uuid}>
                    <TableCell>{item?.request_uuid}</TableCell>
                    <TableCell centerTitle>
                      <StatusSchedule value={item?.status} />
                    </TableCell>
                    <TableCell>{item?.operator_email}</TableCell>
                    <TableCell centerTitle>
                      {dateFormatWithAt(item?.schedule_datetime)}
                    </TableCell>
                    <TableCell centerTitle>{item?.timezone}</TableCell>
                    <Permission value="cancel_image_publication_schedule">
                      <TableCell centerTitle>
                        {item?.status === 'pending' ? (
                          <Typography
                            onClick={() => {
                              modalClose();
                              modalOpen({
                                title: i18n.t(
                                  'CANCEL_PUBLICATION_SCHEDULE_TITLE_KEY'
                                ),
                                content: i18n.t(
                                  'CANCEL_PUBLICATION_SCHEDULE_CONTENT_KEY',
                                  {
                                    datetime: dateFormatWithAt(
                                      item.schedule_datetime
                                    ),
                                  }
                                ),
                                footer: (
                                  <Grid>
                                    <Button
                                      deleteButton
                                      id="cancel-schedule-button"
                                      onClick={() => {
                                        cancelPublicationScheduleRequest({
                                          id,
                                          query,
                                          scheduleUuid: item?.schedule_uuid,
                                        });
                                        modalClose();
                                      }}
                                    >
                                      {i18n.t('CANCEL_SCHEDULE_KEY')}
                                    </Button>
                                    <Button
                                      id="close-cancel-schedule-button"
                                      variant="outlined"
                                      onClick={() => {
                                        modalClose();
                                      }}
                                    >
                                      {i18n.t('BACK_KEY')}
                                    </Button>
                                  </Grid>
                                ),
                              });
                            }}
                            className={classes.cancelButton}
                          >
                            {i18n.t('CANCEL_KEY')}
                          </Typography>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </Permission>
                  </TableRow>
                )}
                data={publicationSchedule}
                isLoading={isLoadingPublicationSchedule}
                overrideRowsCount={10}
                onChangePage={handlePageChange}
                onChangeLimit={handleLimitChange}
                notFoundText={i18n.t('NO_SCHEDULE_FOUND')}
              />
            </Grid>
          </Permission>
        </Grid>
      </Grid>
    </>
  );
};

const mapStateToProps = ({
  environments: {
    environmentSelected,
    publicationSchedule,
    isLoadingPublicationSchedule,
  },
  authenticate: { data },
}: ApplicationState) => ({
  environmentSelected,
  publicationSchedule,
  isLoadingPublicationSchedule,
  permissions: data?.permissions,
});

export default connect(mapStateToProps, {
  setPublicationScheduleRequest,
  cancelPublicationScheduleRequest,
  getPublicationScheduleRequest,
  modalOpen,
  modalClose,
})(SchedulePublication);
